import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, RotateCw, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { EmptyState } from "@/components/states/EmptyState";
import { toast } from "sonner";

const EVENT_LABELS: Record<string, string> = {
  "memory.retrieval.completed": "Memória recuperada",
  "memory.ambiguity.detected": "Ambiguidade detectada",
  "memory.candidate.quarantined": "Memória em quarentena",
  "group.state.classified": "Estado do grupo classificado",
  "threat.detected": "Ameaça detectada",
  "conflict.detected": "Conflito detectado",
  "conflict.inhibition.decided": "Resposta regulada",
  "learning.weight.updated": "Aprendizado atualizado",
  "interoceptive.state.updated": "Sensibilidade ajustada",
  "input.gating.decided": "Mensagem priorizada",
  "tom.model.updated": "Modelo do interlocutor atualizado",
};

interface TelemetryEvent {
  id: number | string;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
  group_id: string | null;
  flag_key: string | null;
  source: string | null;
}

export default function GovernancaForense() {
  const [decisionId, setDecisionId] = useState("");
  const [activeQuery, setActiveQuery] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["forense", activeQuery],
    enabled: activeQuery.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telemetry_events" as never)
        .select("id, event_type, payload, created_at, group_id, flag_key, source")
        .or(`payload->>decision_id.eq.${activeQuery},id.eq.${activeQuery}`)
        .order("created_at", { ascending: true })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as TelemetryEvent[];
    },
  });

  const handleSearch = () => setActiveQuery(decisionId.trim());

  const handleExport = async () => {
    if (!data || data.length === 0) return;
    const payload = {
      decision_id: activeQuery,
      generated_at: new Date().toISOString(),
      events: data,
    };
    const json = JSON.stringify(payload, null, 2);
    // SHA-256 signature (browser-side)
    const buf = new TextEncoder().encode(json);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    const sig = Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    const signed = JSON.stringify({ ...payload, signature_sha256: sig }, null, 2);
    const blob = new Blob([signed], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trace-${activeQuery}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export gerado com assinatura SHA-256");
  };

  const handleReplay = async () => {
    toast.info("Replay em sandbox iniciado (sem efeitos colaterais).");
    // Wired later to edge function trace-replay-sandbox
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Governança · Forense</h1>
        <p className="text-sm text-muted-foreground">
          Visualize a linha do tempo completa de eventos de uma decisão e exporte para auditoria.
        </p>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Buscar trace</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input
            value={decisionId}
            onChange={(e) => setDecisionId(e.target.value)}
            placeholder="ID da decisão ou evento"
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={!decisionId.trim()}>
            <Search className="h-4 w-4 mr-1" />
            Buscar
          </Button>
        </CardContent>
      </Card>

      {isLoading && <LoadingSkeleton />}

      {!isLoading && activeQuery && (!data || data.length === 0) && (
        <EmptyState title="Nenhum evento" description="Nenhum trace encontrado para este identificador." />
      )}

      {data && data.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{data.length} evento(s) na linha do tempo</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReplay}>
                <RotateCw className="h-4 w-4 mr-1" />
                Replay sandbox
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-1" />
                Exportar JSON assinado
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ol className="relative border-l-2 border-border pl-6 space-y-4">
              {data.map((ev, idx) => (
                <li key={String(ev.id) + idx} className="relative">
                  <span className="absolute -left-[31px] top-1 h-4 w-4 rounded-full bg-primary border-4 border-background" />
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline">{EVENT_LABELS[ev.event_type] ?? ev.event_type}</Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(ev.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <details className="rounded-md bg-muted/30 px-3 py-2 text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Snapshot do evento</summary>
                    <pre className="mt-2 overflow-auto text-[11px]">{JSON.stringify(ev.payload, null, 2)}</pre>
                  </details>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
